import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import i18n from '../../utils/i18n';
import SortingAndFiltering from '../forms/SortingAndFiltering';
import ActionButton from '@adsk/bim360-matrix-react-action-button';
import IconButton from '@adsk/bim360-matrix-react-icon-button';
import SvgIcon from '@adsk/bim360-matrix-react-svg-icon';
import Tooltip from '@adsk/bim360-matrix-react-tooltip';
import Text from '@adsk/bim360-matrix-react-text';
import exportIcon from '../../assets/images/icons/icon-export.svg';
import alertWarning from '../../assets/images/icons/alert-warning-fill-red.svg';
import featureFlags, {FEATURE_FLAGS} from '../../utils/featureFlags';
import {isFedrampRegion} from '../../utils/region-helpers';
import BcfModal from '../common/BcfModal';
import {isSupportedUnitAndType} from '../../utils/bcf-helpers';
import {reportFormats} from '../../constants/analytics';

const EXPORT_OPTIONS = {
  PDF_EXPORT: 'PDF_EXPORT',
  XLSX_EXPORT: 'XLSX_EXPORT',
  CSV_REPORT: 'CSV_REPORT',
  EXPORT_AS_BCF: 'EXPORT_AS_BCF'
};

const CSV_EXPORT_TOOLTIP = {zIndex: 1080, whiteSpace: 'pre-wrap'};

class IssuesPanelHeader extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      showBcfModal: false
    };
  }
  checkShowBcfExport(modelUnit, modelType) {
    let showBcfExport = false;
    const newBcfEnabled = featureFlags.get(FEATURE_FLAGS.BIM360_BCF_REPORT);
    const oldBcfEnabled = featureFlags.get(FEATURE_FLAGS.BCF_EXPORT_ENABLED);

    const bothBcfEnabled = newBcfEnabled && oldBcfEnabled;

    if (bothBcfEnabled && isSupportedUnitAndType(modelUnit, modelType)) {
      showBcfExport = true;
    } else if (bothBcfEnabled && !isSupportedUnitAndType(modelUnit, modelType)) {
      showBcfExport = false;
    } else if ((newBcfEnabled && isSupportedUnitAndType(modelUnit, modelType)) || oldBcfEnabled) {
      showBcfExport = true;
    }
    return showBcfExport;
  }
  _getExportOptions() {
    const ACTION_BUTTON_OPTIONS = [];
    const {issuesCount} = this.props;
    const disabled = issuesCount === 0 ? true : false;
    const {modelUnit, modelType} = this.props.getModelTypeAndUnit();
    const customActionButton = (
      <div className="csvExport">
        <Tooltip
          content={<Text hasHTML>{i18n.t('export.csvDecommissioned')}</Text>}
          place={Tooltip.Places.RIGHT}
          style={CSV_EXPORT_TOOLTIP}
          getTargetProps={() => ({className: 'csvTooltipTarget'})}
        >
          <SvgIcon width="20px" height="20px">
            {alertWarning}
          </SvgIcon>
          <span className="csvExportLabel">{i18n.t('export.csvReport')}</span>
        </Tooltip>
      </div>
    );
    if (!isFedrampRegion() && !featureFlags.get(FEATURE_FLAGS.REMOVE_CSV_EXPORT)) {
      ACTION_BUTTON_OPTIONS.push({
        key: EXPORT_OPTIONS.CSV_REPORT,
        label: customActionButton,
        disabled
      });
    }

    if (this.checkShowBcfExport(modelUnit, modelType) && !isFedrampRegion()) {
      ACTION_BUTTON_OPTIONS.push({
        key: EXPORT_OPTIONS.EXPORT_AS_BCF,
        label: i18n.t('export.exportAsBCF'),
        svg: exportIcon,
        disabled
      });
    }
    if (this.props.addXlsxPdfExportOptions) {
      ACTION_BUTTON_OPTIONS.push(
        {
          key: EXPORT_OPTIONS.PDF_EXPORT,
          label: i18n.t('export.exportPdf'),
          svg: exportIcon,
          disabled
        },
        {
          key: EXPORT_OPTIONS.XLSX_EXPORT,
          label: i18n.t('export.exportXlsx'),
          svg: exportIcon,
          disabled
        }
      );
    }

    return ACTION_BUTTON_OPTIONS;
  }

  _renderExportSingleChoice(option) {
    return (
      <Tooltip
        theme={Tooltip.Themes.DARK}
        place={Tooltip.Places.TOP}
        container={this.props.container}
        content={this.props.isFetchReportLoading ? i18n.t('export.exporting') : option.label}
      >
        <IconButton
          className="IssuesPanelHeader__export-button"
          onClick={() => this._actionButtonOnSelect(option.key)}
          disabled={this.props.isFetchReportLoading}
        >
          <SvgIcon width="20px" height="20px">
            {exportIcon}
          </SvgIcon>
        </IconButton>
      </Tooltip>
    );
  }

  _renderExportMultipleChoices(options) {
    return (
      <ActionButton
        options={options}
        container={this.props.container}
        onSelect={this._actionButtonOnSelect.bind(this)}
      />
    );
  }

  _actionButtonOnSelect(eventKey) {
    // eslint-disable-next-line default-case
    switch (eventKey) {
      case EXPORT_OPTIONS.CSV_REPORT:
        this.props.fetchIssuesReport();
        break;
      case EXPORT_OPTIONS.EXPORT_AS_BCF:
        featureFlags.get(FEATURE_FLAGS.BIM360_BCF_REPORT)
          ? this.setState({showBcfModal: true})
          : this.props.createBcfFileAction();
        break;
      case EXPORT_OPTIONS.PDF_EXPORT:
        this.props.onExportOptionClick(reportFormats.PDF);
        break;
      case EXPORT_OPTIONS.XLSX_EXPORT:
        this.props.onExportOptionClick(reportFormats.XLSX);
        break;
    }
  }

  _renderExportButton() {
    const exportOptions = this._getExportOptions();
    let exportJsx;

    switch (exportOptions.length) {
      case 0:
        exportJsx = null;
        break;
      case 1:
        exportJsx = this._renderExportSingleChoice(exportOptions[0]);
        break;
      default:
        exportJsx = this._renderExportMultipleChoices(exportOptions);
    }

    return <div className="IssuesPanelHeader__export">{exportJsx}</div>;
  }

  onCancel = () => {
    this.setState({showBcfModal: false});
  };

  render() {
    const {container} = this.props;
    return (
      <div className="IssuesPanelHeader">
        <SortingAndFiltering container={container} />
        {this._renderExportButton()}
        {this.state.showBcfModal && (
          <BcfModal container={container} closeModal={this.onCancel} showIssueNameInFileName={false} />
        )}
      </div>
    );
  }
}

IssuesPanelHeader.displayName = 'IssuesPanelHeader';

IssuesPanelHeader.propTypes = {
  isFetchReportLoading: PropTypes.bool,
  fetchIssuesReport: PropTypes.func,
  createBcfFileAction: PropTypes.func,
  getModelTypeAndUnit: PropTypes.func,
  container: PropTypes.object,
  onExportOptionClick: PropTypes.func.isRequired,
  addXlsxPdfExportOptions: PropTypes.bool.isRequired,
  issuesCount: PropTypes.number
};

export default IssuesPanelHeader;
